import React, { useState } from 'react';
import { FaCalendar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useRoster } from './hooks/useRoster';
import { StaffCell } from './StaffCell';
import './CalendarView.css';

const CalendarView = () => {
  const {
    shifts,
    rosterEntries,
    staff,
    currentView,
  } = useRoster();

  const [currentDate, setCurrentDate] = useState(new Date(currentView.startDate));

  const getStaffForShift = (date, shiftId) => {
    return rosterEntries
      .filter(entry => entry.date === date && entry.shiftId === shiftId)
      .map(entry => staff.find(s => s.id === entry.staffId))
      .filter(Boolean);
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear(),
    };
  };

  const getWeekDates = (startDate) => {
    const week = [];
    const start = new Date(startDate);
    start.setDate(start.getDate() - start.getDay()); // Start from Sunday
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      week.push(formatDate(date));
    }
    return week;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const weekDates = getWeekDates(currentDate);
  const currentWeek = formatDisplayDate(weekDates[0]);

  const getShiftTypeClass = (shiftType) => {
    switch (shiftType) {
      case 'morning': return 'shift-morning';
      case 'afternoon': return 'shift-afternoon';
      case 'night': return 'shift-night';
      default: return 'shift-off';
    }
  };

  return (
    <div className="calendar-view">
      {/* Calendar Header */}
      <div className="calendar-card">
        <div className="calendar-header">
          <div className="calendar-header-content">
            <div className="calendar-title">
              <FaCalendar className="title-icon" />
              Weekly Calendar View
            </div>
            <div className="calendar-navigation">
              <button
                className="nav-btn"
                onClick={() => navigateWeek('prev')}
              >
                <FaChevronLeft className="nav-icon" />
                Previous Week
              </button>
              <div className="current-week">
                {currentWeek.month} {currentWeek.year}
              </div>
              <button
                className="nav-btn"
                onClick={() => navigateWeek('next')}
              >
                Next Week
                <FaChevronRight className="nav-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-card">
        <div className="calendar-content">
          <div className="calendar-grid-header">
            {/* Time column header */}
            <div className="time-header">
              Shifts
            </div>
            {/* Date headers */}
            {weekDates.map(date => {
              const dateInfo = formatDisplayDate(date);
              return (
                <div
                  key={date}
                  className="date-header"
                >
                  <div className="day-name">{dateInfo.dayName}</div>
                  <div className="day-number">{dateInfo.day}</div>
                  <div className="month">{dateInfo.month}</div>
                </div>
              );
            })}
          </div>

          {/* Shift rows */}
          {shifts.map(shift => (
            <div key={shift.id} className="shift-row">
              {/* Shift info column */}
              <div className={`shifts-info ${getShiftTypeClass(shift.type)}`}>
                <div className="shift-name">{shift.name}</div>
                <div className="shift-time">
                  {shift.startTime} - {shift.endTime}
                </div>
                <span className="staff-cal-badge">
                  Need {shift.requiredStaff}
                </span>
              </div>
              
              {/* Date columns */}
              {weekDates.map(date => {
                const assignedStaff = getStaffForShift(date, shift.id);
                const isUnderstaffed = assignedStaff.length < shift.requiredStaff;
                
                return (
                  <div
                    key={`${date}-${shift.id}`}
                    className={`date-cell ${isUnderstaffed ? 'understaffed' : ''}`}
                  >
                    <div className="staff-list">
                      {assignedStaff.map(staff => staff && (
                        <StaffCell
                          key={staff.id}
                          staff={staff}
                          date={date}
                          shiftId={shift.id}
                          showActions
                          compact
                        />
                      ))}
                      {/* Show empty slots */}
                      {Array.from({ 
                        length: Math.max(0, shift.requiredStaff - assignedStaff.length) 
                      }).map((_, i) => (
                        <StaffCell
                          key={`empty-${i}`}
                          staff={null}
                          date={date}
                          shiftId={shift.id}
                          showActions
                          compact
                        />
                      ))}
                    </div>
                    
                    {isUnderstaffed && assignedStaff.length > 0 && (
                      <div className="understaffed-warning">
                        Need {shift.requiredStaff - assignedStaff.length} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;